import { useWindowSize } from "hooks/useWindowSize";
import classNames from "classnames";
import randomBytes from "randombytes";
import { COMMENT_TYPE } from "store/apis/notes";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { CategoryMultiSelect } from "components/DesignSystem/CategoryMultiSelect/CategoryMultiSelect";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Header } from "components/DesignSystem/Header/Header";
import Modal from "components/DesignSystem/Modal/Modal";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { BareInput, Label } from "components/DesignSystem/TextInput/TextInput";
import { EmptyMetrics } from "components/EmptyMetrics/EmptyMetrics";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { ArrowDown } from "components/icons/ArrowDown";
import { ArrowRight } from "components/icons/ArrowRight";
import { EditPencil } from "components/icons/EditPencil";
import { ReportFilter } from "components/Reports/ReportFilter";
import { ACCOUNTING_METHODS, INCOME_STATEMENT } from "constants/bookkeeping";
import { PERCENTAGE_MODE_OPTIONS, VIEW_BY_OPTIONS } from "constants/reports";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useFilters } from "hooks/useFilter";
import { useModal } from "hooks/useModal";
import { useQuery } from "hooks/useQuery";
import React, { RefObject, useEffect, useMemo, useRef, useState } from "react";
import {
  createColumnHelper,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { flattenTypes } from "utils/flattenCOA";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";
import { getPinnedShadowClass } from "utils/getPinnedShadowClass";

import * as Popover from "@radix-ui/react-popover";
import { AddCommentPop } from "components/AddComment/AddComment";
import { AddCommentAnchor } from "components/AddCommentPopoverAnchor/AddCommentPopoverAnchor";
import { AddCommentPopoverRoot } from "components/AddCommentPopoverRoot/AddCommentPopoverRoot";
import Loader from "components/DesignSystem/Loader/loader";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import Table from "components/DesignSystem/Table/V2/Table";
import { DeleteIcon } from "components/icons/delete";
import { SmallPlusIcon } from "components/icons/export const SmallPlusIcon";
import { CustomReportBarChart } from "components/Reports/CustomReports/CustomReportBarChart";
import { CustomReportDownload } from "components/Reports/CustomReports/CustomReportViewOnly";
import type { ReportFilterType } from "components/Reports/ReportFilter";
import Tab from "components/DesignSystem/Tab/Tab";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { motion } from "framer-motion";
import { useAppSelector } from "hooks/useAppSelector";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";
import { useToast } from "hooks/useToast";
import { useUnsavedChanges } from "hooks/useUnsavedChanges";
import { useHistory, useLocation } from "react-router-dom";
import { UnsavedChangesModal } from "components/UnsavedChangesModal/UnsavedChangesModal";
import ThreeDots from "static/images/ThreeDots.svg";
import {
  ANALYSIS_MODE,
  CHART_TYPE,
  CUSTOM_REPORT_MODE,
  CustomReportChartResponse,
  PreviewGroup,
  ReportPeriod,
  ChartConfig,
  SERIES_MODE,
} from "store/apis/customReports";
import {
  useGetCustomReportsQuery,
  useGetCustomReportByIdQuery,
  useUpdateCustomReportMutation,
  usePreviewCustomReportMutation,
  usePreviewCustomReportChartMutation,
} from "store/apis/customReports";
import { BackendError } from "types/utils/error";
import { stopPropagation } from "utils/stopPropagation";
import { CustomReportEmptyStateIcon } from "components/icons/CustomReportEmptyStateIcon";
import { useGetCategoryByIdentifierQuery } from "store/apis/chartOfAccounts";
import {
  EXPENSE_CATEGORY_IDENTIFIER,
  REVENUE_CATEGORY_IDENTIFIER,
} from "constants/categoryIdentifier";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import { PERIOD } from "constants/revenueMetrics";

const DEFAULT_FILTER: ReportFilterType = {
  START_DATE: dayjs().startOf("year").format(YYYY_MM_DD),
  END_DATE: dayjs().format(YYYY_MM_DD),
  ACCOUNTING_METHOD: ACCOUNTING_METHODS.ACCRUAL,
  VIEW_BY: null,
  HIDE_ZERO: true,
  TXN_GROUP_ID: null,
  CLASS_IDS: [],
  CLASSIFICATION: [],
  NO_CLASSIFICATION: [],
  OTHER: null,
  COMPARE_BY: null,
  VARIANCE: [],
  PERCENTAGE_MODE: null,
  FORECAST_ENABLED: false,
  FORECAST_START: "",
  FORECAST_END: "",
  FORECAST_SET_ID: null,
};

const CategoryGroupModal = ({
  isOpen,
  close,
  entityId,
  groupId,
  editGroup,
  onSave,
}: {
  isOpen: boolean;
  close: () => void;
  entityId: string;
  groupId: string;
  editGroup?: PreviewGroup | null;
  onSave: (group: PreviewGroup) => void;
}) => {
  const isEditMode = Boolean(editGroup);

  const [groupTitle, setGroupTitle] = useState(editGroup?.title || "");
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(
    editGroup?.category_ids || []
  );

  const { data: categoryData } = useGetCategoryByIdentifierQuery({
    groupId,
    entityId,
    identifiers: [REVENUE_CATEGORY_IDENTIFIER, EXPENSE_CATEGORY_IDENTIFIER],
  });

  const rootCategoryIds = categoryData
    ?.map((category) => category.uuid)
    .join(",");

  const handleSubmit = () => {
    onSave({
      ...(editGroup ? { uuid: editGroup.uuid } : {}),
      title: groupTitle,
      category_ids: selectedCategoryIds,
    });

    handleClose();
  };

  const handleClose = () => {
    setGroupTitle("");
    setSelectedCategoryIds([]);
    close();
  };

  const isSubmitDisabled =
    !groupTitle.trim() || selectedCategoryIds.length === 0;

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="large" useCustomOverlay>
        <Modal.Header>
          <Modal.Title>
            {isEditMode ? "Edit category group" : "Add category group"}
          </Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="t-flex t-flex-col t-gap-5">
          <div className="t-flex t-flex-col t-gap-1.5">
            <Label className="!t-pb-0" required>
              Group title
            </Label>
            <BareInput
              placeholder="Enter group name"
              value={groupTitle}
              onChange={(e) => setGroupTitle(e.target.value)}
            />
          </div>

          <div className="t-flex t-flex-col t-gap-2">
            <Label className="!t-pb-0" required>
              Categories to group
            </Label>

            <CategoryMultiSelect
              selectedUuids={selectedCategoryIds}
              onChange={setSelectedCategoryIds}
              hiddenCategoryTypes={["BANK_TRANSFER", "PAY_DOWN_CREDIT"]}
              rootCategoryId={rootCategoryIds}
            />
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Button customType="secondary" size="regular" onClick={handleClose}>
            Back
          </Button>
          <Button
            customType="primary"
            size="regular"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            {isEditMode ? "Save" : "Add"}
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};

const CategoryGroupBox = ({
  group,
  allCategories,
  isCategoriesLoading,
  onEdit,
  onDelete,
}: {
  group: { title: string; category_ids: string[] };
  allCategories: { uuid: string; name: string }[];
  isCategoriesLoading: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}) => (
  <div className="t-flex t-flex-col t-rounded-lg t-bg-neutral-0 t-pb-3">
    <div className="t-flex t-items-center t-justify-between t-px-4 t-py-3">
      <span className="t-text-subtext t-text-text-100">{group.title}</span>
      {(onEdit || onDelete) && (
        <div className="t-flex t-items-center t-gap-1">
          {onEdit && (
            <Button customType="ghost_icon" size="small" onClick={onEdit}>
              <EditPencil />
            </Button>
          )}
          {onDelete && (
            <Button customType="ghost_icon" size="small" onClick={onDelete}>
              <DeleteIcon size="14" />
            </Button>
          )}
        </div>
      )}
    </div>

    <div className="t-flex t-flex-wrap t-gap-2 t-px-3">
      {group.category_ids.map((id) => {
        if (isCategoriesLoading) {
          return <Skeleton key={id} className="t-h-5 t-w-20" />;
        }

        const name = allCategories.find((c) => c.uuid === id)?.name || id;
        return (
          <ToolTip key={id} text={name}>
            <div className="t-max-w-[200px]">
              <Tag tagType="dark_gray" icon={false}>
                <span className="t-truncate t-block">{name}</span>
              </Tag>
            </div>
          </ToolTip>
        );
      })}
    </div>
  </div>
);

const SidebarDropdown = ({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Dropdown.Root modal={false}>
    <Dropdown.Trigger asChild>
      <button className="t-flex t-w-full t-h-8 t-items-center t-justify-between t-px-3 t-rounded-md t-border t-border-solid t-border-neutral-10 t-bg-white t-text-body-sm t-font-medium t-text-text-60  hover:t-bg-surface-grey t-group t-transition-all t-duration-200 disabled:t-pointer-events-none disabled:t-opacity-50">
        <span className="t-flex-1 t-text-left t-truncate">{label}</span>
        <span className="t-transition-transform t-duration-200 group-data-[state=open]:t-rotate-180">
          <ArrowDown color="currentColor" strokeWidth="1.5" />
        </span>
      </button>
    </Dropdown.Trigger>
    <Dropdown.Portal>
      <Dropdown.Content
        sideOffset={6}
        style={{
          width: "var(--radix-dropdown-menu-trigger-width)",
          minWidth: 0,
        }}
      >
        {children}
      </Dropdown.Content>
    </Dropdown.Portal>
  </Dropdown.Root>
);

const ConfigureSidebar = ({
  onAddCategoryGroup,
  groups = [],
  onEditGroup,
  onDeleteGroup,
  filter,
  updateFilter,
  chartConfig,
  onChartConfigChange,
  hasWritePermission,
}: {
  onAddCategoryGroup: () => void;
  groups?: PreviewGroup[];
  onEditGroup: (group: PreviewGroup) => void;
  onDeleteGroup: (groupId: string) => void;
  filter: ReportFilterType;
  updateFilter: (name: keyof ReportFilterType, value: any) => void;
  chartConfig: ChartConfig;
  onChartConfigChange: (update: Partial<ChartConfig>) => void;
  hasWritePermission: boolean;
}) => {
  const { isMobile } = useWindowSize();
  const isPercentageModeEnabled = Boolean(filter.PERCENTAGE_MODE);
  const [activeTab, setActiveTab] = useState("CONFIGURE");

  const { chartOfAccounts, isFetching, isSuccess } = useChartOfAccounts({});
  const isCategoriesLoading = isFetching || !isSuccess;
  const allCategories = flattenTypes({ accounts: chartOfAccounts });

  const selectedGroupByLabel = VIEW_BY_OPTIONS.filter(
    ({ value }) => value !== PERIOD.TOTALS
  ).find((o) => o.value === filter.VIEW_BY)?.label;

  const selectedCalculationLabel = PERCENTAGE_MODE_OPTIONS.find(
    (o) => o.value === filter.PERCENTAGE_MODE
  )?.label;

  const selectedSeriesLabel = chartConfig.series.group_id
    ? groups.find((g) => g.uuid === chartConfig.series.group_id)?.title
    : "All category groups";

  const selectedYAxisLabel =
    chartConfig.y_axis === ANALYSIS_MODE.PERCENT
      ? selectedCalculationLabel || "% Calculation"
      : "Amount (USD)";

  return (
    <div className="t-flex t-w-72 sm:t-w-full t-flex-shrink-0 t-flex-col t-bg-surface-lighter-grey t-mr-4 sm:t-mr-0 sm:t-mb-4 t-pb-3 t-pt-1 t-overflow-hidden">
      <Tab.Root
        value={activeTab}
        onValueChange={setActiveTab}
        className="t-flex t-flex-col t-flex-1 t-min-h-0"
      >
        <Tab.List className="t-px-3">
          <Tab.Trigger value="CONFIGURE" className="!t-text-subtext-sm">
            Configure
          </Tab.Trigger>
          <Tab.Trigger value="CUSTOMIZE_CHART" className="!t-text-subtext-sm">
            Customize chart
          </Tab.Trigger>
        </Tab.List>

        <Tab.Content
          value="CONFIGURE"
          className="t-flex t-flex-col t-flex-1 t-overflow-hidden t-p-3"
        >
          <div className="t-flex t-flex-col t-gap-4 t-flex-1 t-overflow-hidden">
            <div className="t-flex t-flex-col t-gap-1.5">
              <span className="t-text-subtext-sm t-text-text-30">Group by</span>
              <SidebarDropdown label={selectedGroupByLabel || "None"}>
                <Dropdown.Item
                  onSelect={() => updateFilter("VIEW_BY", PERIOD.TOTALS)}
                  className={classNames({
                    "t-bg-purple-0": filter.VIEW_BY === PERIOD.TOTALS,
                  })}
                >
                  None
                </Dropdown.Item>
                {VIEW_BY_OPTIONS.filter(
                  ({ value }) => value !== PERIOD.TOTALS
                ).map(({ label, value }) => (
                  <Dropdown.Item
                    key={value}
                    onSelect={() => updateFilter("VIEW_BY", value)}
                    className={classNames({
                      "t-bg-purple-0": filter.VIEW_BY === value,
                    })}
                  >
                    {label}
                  </Dropdown.Item>
                ))}
              </SidebarDropdown>
            </div>

            <div className="t-flex t-flex-col t-gap-1.5">
              <span className="t-text-subtext-sm t-text-text-30">
                Calculations
              </span>
              <SidebarDropdown label={selectedCalculationLabel || "None"}>
                <Dropdown.Item
                  onSelect={() => {
                    updateFilter("PERCENTAGE_MODE", null);
                    if (chartConfig.y_axis === ANALYSIS_MODE.PERCENT) {
                      onChartConfigChange({ y_axis: ANALYSIS_MODE.AMOUNT });
                    }
                  }}
                  className={classNames({
                    "t-bg-purple-0": !filter.PERCENTAGE_MODE,
                  })}
                >
                  None
                </Dropdown.Item>
                {PERCENTAGE_MODE_OPTIONS.map((opt) => {
                  const calculationItem = (
                    <Dropdown.Item
                      onSelect={() =>
                        updateFilter("PERCENTAGE_MODE", opt.value)
                      }
                      className={classNames({
                        "t-bg-purple-0": filter.PERCENTAGE_MODE === opt.value,
                      })}
                    >
                      <div className="t-flex t-flex-col t-gap-0.5">
                        {opt.label}
                        {isMobile && (
                          <span className="t-whitespace-normal t-text-caption t-text-text-30">
                            {opt.tooltip}
                          </span>
                        )}
                      </div>
                    </Dropdown.Item>
                  );

                  if (isMobile) {
                    return <div key={opt.value}>{calculationItem}</div>;
                  }

                  return (
                    <ToolTip
                      key={opt.value}
                      sideOffset={4}
                      text={
                        <div className="t-w-60 t-text-left">{opt.tooltip}</div>
                      }
                      side="right"
                    >
                      {calculationItem}
                    </ToolTip>
                  );
                })}
              </SidebarDropdown>
            </div>
            <div className="t-flex t-flex-col t-gap-1.5 t-flex-1 t-min-h-0 t-overflow-hidden">
              <div className="t-flex t-items-center t-justify-between">
                <span className="t-text-subtext-sm t-text-text-30">
                  Category group
                </span>
                <LockedUi feature={WRITE_PERMISSION_KEYS.FINANCIALS}>
                  <Button
                    customType="ghost_icon"
                    size="xxsmall"
                    onClick={onAddCategoryGroup}
                  >
                    <SmallPlusIcon className="t-size-3" />
                  </Button>
                </LockedUi>
              </div>
              {groups.length > 0 && (
                <div className="t-flex t-flex-1 t-flex-col t-gap-3 t-min-h-0 t-overflow-y-auto ">
                  {groups.map((group) => (
                    <CategoryGroupBox
                      key={group.uuid}
                      group={group}
                      allCategories={allCategories}
                      isCategoriesLoading={isCategoriesLoading}
                      onEdit={
                        hasWritePermission
                          ? () => onEditGroup(group)
                          : undefined
                      }
                      onDelete={
                        hasWritePermission
                          ? () => onDeleteGroup(group.uuid || "")
                          : undefined
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </Tab.Content>

        <Tab.Content
          value="CUSTOMIZE_CHART"
          className="t-flex t-flex-col t-gap-4 t-p-3 t-overflow-y-auto t-flex-1"
        >
          <div className="t-flex t-flex-col t-gap-1.5">
            <span className="t-text-subtext-sm t-text-text-30">Chart type</span>
            <SidebarDropdown label="Bar Chart">
              <Dropdown.Item className="t-bg-purple-0">Bar Chart</Dropdown.Item>
            </SidebarDropdown>
          </div>

          <div className="t-flex t-flex-col t-gap-1.5">
            <span className="t-text-subtext-sm t-text-text-30">X-axis</span>
            <SidebarDropdown label="Period">
              <Dropdown.Item className="t-bg-purple-0">Period</Dropdown.Item>
            </SidebarDropdown>
          </div>

          <div className="t-flex t-flex-col t-gap-1.5">
            <span className="t-text-subtext-sm t-text-text-30">Y-axis</span>
            <SidebarDropdown label={selectedYAxisLabel}>
              <Dropdown.Item
                onSelect={() =>
                  onChartConfigChange({ y_axis: ANALYSIS_MODE.AMOUNT })
                }
                className={classNames({
                  "t-bg-purple-0": chartConfig.y_axis === ANALYSIS_MODE.AMOUNT,
                })}
              >
                Amount (USD)
              </Dropdown.Item>
              <ConditionalToolTip
                condition={
                  !isPercentageModeEnabled && (
                    <div className="t-w-52 t-text-left">
                      Enable by selecting a calculation from Configure tab
                    </div>
                  )
                }
                side="right"
              >
                <span>
                  <Dropdown.Item
                    disabled={!isPercentageModeEnabled}
                    onSelect={() =>
                      onChartConfigChange({ y_axis: ANALYSIS_MODE.PERCENT })
                    }
                    className={classNames({
                      "t-bg-purple-0":
                        chartConfig.y_axis === ANALYSIS_MODE.PERCENT,
                    })}
                  >
                    {selectedCalculationLabel || "% Calculation"}
                  </Dropdown.Item>
                </span>
              </ConditionalToolTip>
            </SidebarDropdown>
          </div>

          <div className="t-flex t-flex-col t-gap-1.5">
            <span className="t-text-subtext-sm t-text-text-30">Series</span>
            <SidebarDropdown label={selectedSeriesLabel}>
              <Dropdown.Item
                onSelect={() =>
                  onChartConfigChange({
                    series: { mode: SERIES_MODE.GROUPS, group_id: null },
                  })
                }
                className={classNames({
                  "t-bg-purple-0": !chartConfig.series.group_id,
                })}
              >
                All category groups
              </Dropdown.Item>
              {groups.length > 0 && <Dropdown.Separator />}
              {groups.length > 0 && (
                <ToolTip text="Select specific groups to see breakdown">
                  <span>
                    <Dropdown.Label className="!t-text-text-30">
                      Select specific groups to see breakdown
                    </Dropdown.Label>
                  </span>
                </ToolTip>
              )}
              {groups.map((group) => (
                <Dropdown.Item
                  key={group.uuid}
                  onSelect={() =>
                    onChartConfigChange({
                      series: {
                        mode: SERIES_MODE.CATEGORIES,
                        group_id: group.uuid ?? null,
                      },
                    })
                  }
                  className={classNames({
                    "t-bg-purple-0": chartConfig.series.group_id === group.uuid,
                  })}
                >
                  {group.title}
                </Dropdown.Item>
              ))}
            </SidebarDropdown>
          </div>
        </Tab.Content>
      </Tab.Root>
    </div>
  );
};

const CommentableThreeDotsMenu = ({
  contentId,
  reportId,
  reportGroupId,
}: {
  contentId: string;
  reportId: string;
  reportGroupId: string;
}) => {
  const popoverId = `${reportGroupId}-${contentId}`;

  return (
    <AddCommentPopoverRoot contentId={popoverId}>
      <Dropdown.Root>
        <Dropdown.Trigger asChild onClick={stopPropagation}>
          <div>
            <AddCommentAnchor contentId={popoverId}>
              <Button customType="ghost_icon" size="xsmall">
                <img src={ThreeDots} alt="Action" />
              </Button>
            </AddCommentAnchor>
          </div>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content onClick={stopPropagation} align="end">
            <Popover.Trigger asChild>
              <Dropdown.Item>Add comment</Dropdown.Item>
            </Popover.Trigger>
          </Dropdown.Content>
        </Dropdown.Portal>
        <Popover.Portal>
          <AddCommentPop
            commentType={COMMENT_TYPE.CUSTOM_REPORT_COMMENT}
            contentId={contentId}
            reportId={reportId}
            reportGroupId={reportGroupId}
          />
        </Popover.Portal>
      </Dropdown.Root>
    </AddCommentPopoverRoot>
  );
};

type CustomReportRow = {
  uuid: string;
  name: string;
  subRows?: CustomReportRow[];
  monthData: Record<
    string,
    { summary_amount: number; percentage_value: number }
  >;
  reportGroupId?: string;
};

const buildTableData = (
  periodEntries: [string, ReportPeriod][]
): CustomReportRow[] => {
  const firstPeriodGroups = periodEntries[0][1].groups;

  return firstPeriodGroups.map((group) => {
    const groupMonthData: Record<
      string,
      { summary_amount: number; percentage_value: number }
    > = {};
    for (const [periodName, period] of periodEntries) {
      const matchingGroup = period.groups.find((g) => g.uuid === group.uuid);
      groupMonthData[periodName] = {
        summary_amount: matchingGroup?.group_amount || 0,
        percentage_value: matchingGroup?.group_percentage || 0,
      };
    }

    const subRows =
      group.report_categories?.map((category) => {
        const catMonthData: Record<
          string,
          { summary_amount: number; percentage_value: number }
        > = {};
        for (const [periodName, period] of periodEntries) {
          const matchingGroup = period.groups.find(
            (g) => g.uuid === group.uuid
          );
          const matchingCat = matchingGroup?.report_categories?.find(
            (c) => c.uuid === category.uuid
          );
          catMonthData[periodName] = {
            summary_amount: matchingCat?.summary_amount || 0,
            percentage_value: matchingCat?.percentage_value || 0,
          };
        }

        return {
          uuid: category.uuid,
          name: category.name,
          monthData: catMonthData,
          reportGroupId: group.uuid,
        };
      }) || [];

    return {
      uuid: group.uuid,
      name: group.title,
      subRows,
      monthData: groupMonthData,
    };
  });
};

const MultiPeriodTable = ({
  periodEntries,
  showComments = false,
  reportId,
  percentageMode,
  groupBy,
}: {
  periodEntries: [string, ReportPeriod][];
  reportId: string;
  showComments?: boolean;
  percentageMode?: string | null;
  groupBy?: string | null;
}) => {
  const openComment = useAppSelector((state) => state.openComment.openComment);
  const { isMobile } = useWindowSize();
  const columnHelper = createColumnHelper<CustomReportRow>();

  const tableData = useMemo(
    () => buildTableData(periodEntries),
    [periodEntries]
  );

  const [expanded, setExpanded] = useState<ExpandedState>(() =>
    tableData.reduce<Record<string, boolean>>((acc, row) => {
      acc[row.uuid] = true;
      return acc;
    }, {})
  );

  useEffect(() => {
    if (!openComment) return;
    const parentGroup = tableData.find(
      (group) =>
        openComment === group.uuid || openComment.startsWith(`${group.uuid}-`)
    );

    if (parentGroup) {
      setExpanded((prev) =>
        typeof prev === "object" ? { ...prev, [parentGroup.uuid]: true } : prev
      );
    }
  }, [openComment, tableData]);

  const getPeriodHeader = (name: string, period: ReportPeriod) => {
    const isPeriodGroupBy =
      groupBy === PERIOD.MONTHLY ||
      groupBy === PERIOD.QUARTERLY ||
      groupBy === PERIOD.YEARLY;

    if (!isPeriodGroupBy) {
      const start = dayjs(period.start_date).format("MMM DD, YYYY");
      const end = dayjs(period.end_date).format("MMM DD, YYYY");
      return `${start} - ${end}`;
    }
    return name;
  };

  const periodColumns = periodEntries.map(([name, period]) => {
    const headerTitle = getPeriodHeader(name, period);

    if (percentageMode) {
      const percentageModeLabel = PERCENTAGE_MODE_OPTIONS.find(
        (option) => option.value === percentageMode
      )?.label;
      return columnHelper.group({
        id: name,
        header: () => <HeadCellContent title={headerTitle} align="right" />,
        columns: [
          columnHelper.accessor("uuid", {
            id: `${name}_amount`,
            size: 165,
            header: () => <HeadCellContent title="Amount" align="right" />,
            cell: ({ row }) => {
              const amount =
                row.original.monthData?.[name]?.summary_amount || 0;
              return (
                <div className="t-flex t-justify-end t-ml-4">
                  {amount ? <AmountSuperScript amount={Number(amount)} /> : "-"}
                </div>
              );
            },
          }),
          columnHelper.accessor("uuid", {
            id: `${name}_percentage`,
            size: 165,
            header: () => (
              <HeadCellContent
                title={percentageModeLabel || ""}
                align="right"
              />
            ),
            cell: ({ row }) => {
              const percentageValue =
                row.original.monthData?.[name]?.percentage_value;
              return (
                <div className="t-flex t-justify-end t-ml-4">
                  {percentageValue != null && percentageValue !== 0
                    ? `${percentageValue.toFixed(1)}%`
                    : "-"}
                </div>
              );
            },
          }),
        ],
      });
    }

    return columnHelper.accessor("uuid", {
      id: name,
      size: 114,
      header: () => <HeadCellContent title={headerTitle} align="right" />,
      cell: ({ row }) => {
        const amount = row.original.monthData?.[name]?.summary_amount || 0;
        return (
          <div className="t-flex t-justify-end t-ml-4">
            {amount ? <AmountSuperScript amount={Number(amount)} /> : "-"}
          </div>
        );
      },
    });
  });

  const columns = [
    columnHelper.accessor("name", {
      header: () => <HeadCellContent title="Category" align="left" />,
      size: isMobile ? 150 : 300,
      cell: ({ row, getValue }) => {
        const name = getValue();

        return (
          <div
            style={{ paddingLeft: `${row.depth * 1}rem` }}
            className="t-flex t-items-center"
          >
            <div
              className={classNames({
                "t-rotate-90 t-transform t-transition t-duration-300 t-ease-in-out":
                  row.getIsExpanded(),
                "t-invisible": !row.getCanExpand(),
              })}
            >
              <ArrowRight stroke="1.5" color="currentColor" />
            </div>
            <span className="t-ml-1">{name}</span>
          </div>
        );
      },
    }),
    ...periodColumns,
    ...(showComments
      ? [
          columnHelper.display({
            header: "",
            id: "actions",
            cell: ({ row }) =>
              row.original.reportGroupId ? (
                <CommentableThreeDotsMenu
                  contentId={row.original.uuid}
                  reportId={reportId}
                  reportGroupId={row.original.reportGroupId}
                />
              ) : null,
            minSize: 60,
            size: 60,
          }),
        ]
      : []),
  ];

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      expanded,
      columnPinning: {
        left: isMobile ? [] : ["name"],
        ...(showComments ? { right: ["actions"] } : {}),
      },
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.subRows,
    getRowId: (row) =>
      row.reportGroupId ? `${row.reportGroupId}-${row.uuid}` : row.uuid,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const isGroupedHeader = Boolean(percentageMode);

  return (
    <div className="t-pb-10 t-relative">
      <Table.Container className="t-overflow-scroll scrollbar-visible t-scroll-auto t-my-4 t-ml-1 t-mr-8 sm:t-mr-0 sm:t-overflow-y-visible">
        <Table.Content className="t-w-full">
          <Table.Head>
            {table.getHeaderGroups().map((headerGroup) => {
              const isGroupRow = isGroupedHeader && headerGroup.depth === 0;

              return (
                <Table.HeadRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const styles = getCommonPinningStyles(header.column);
                    const { boxShadow } = getPinnedShadowClass(header.column);
                    const isPinnedColumn = header.column.getIsPinned();

                    return (
                      <Table.HeadCell
                        isUpperCase={false}
                        key={header.id}
                        colSpan={header.colSpan}
                        style={{
                          ...styles,
                          minWidth: header.column.getSize(),
                        }}
                        className={classNames(boxShadow, {
                          "!t-bg-surface-lighter-grey": isPinnedColumn,
                          "t-drop-shadow-border-shadow-neutral-0":
                            isPinnedColumn &&
                            ["name", "actions"].includes(header.column.id),
                          "!t-z-[3]":
                            isPinnedColumn &&
                            ["name", "actions"].includes(header.column.id),
                          "!t-text-surface-lighter-grey":
                            isGroupRow && header.column.id === "name",
                          "t-border-r t-border-solid t-border-neutral-0":
                            isGroupedHeader &&
                            (isGroupRow ||
                              header.column.id.endsWith("_amount") ||
                              header.column.id.endsWith("_percentage")),
                        })}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </Table.HeadCell>
                    );
                  })}
                </Table.HeadRow>
              );
            })}
          </Table.Head>
          <Table.Body>
            {table.getRowModel().rows.map((row) => (
              <motion.tr
                key={row.id}
                layout
                onClick={() => row.getCanExpand() && row.toggleExpanded()}
                className={classNames(
                  "t-select-none t-px-3 t-text-body-sm t-group t-border t-border-b t-border-solid t-border-t-0 t-border-x-0 t-border-neutral-0",
                  {
                    "t-text-text-100": row.depth === 0,
                    "t-text-text-60": row.depth === 1,
                    "t-cursor-pointer hover:t-bg-surface-lighter-grey":
                      row.getCanExpand(),
                    "animate-transaction-row": row.original.reportGroupId
                      ? openComment ===
                        `${row.original.reportGroupId}-${row.original.uuid}`
                      : openComment === row.original.uuid,
                  }
                )}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
              >
                {row.getVisibleCells().map((cell) => {
                  const styles = getCommonPinningStyles(cell.column);
                  const { boxShadow } = getPinnedShadowClass(cell.column);

                  return (
                    <Table.Cell
                      key={cell.id}
                      style={{
                        ...styles,
                        minWidth: cell.column.getSize(),
                        ...(cell.column.id === "name"
                          ? { width: "100px", minWidth: "100px" }
                          : {}),
                        ...(cell.column.id === "actions"
                          ? { width: "30px", minWidth: "30px" }
                          : {}),
                      }}
                      className={classNames(boxShadow, {
                        "t-drop-shadow-border-shadow-neutral-0":
                          cell.column.getIsPinned() &&
                          ["name", "actions"].includes(cell.column.id),
                        "!t-z-[3]":
                          cell.column.getIsPinned() &&
                          ["name", "actions"].includes(cell.column.id),
                        "t-border-r t-border-solid t-border-neutral-0":
                          isGroupedHeader &&
                          (cell.column.id.endsWith("_amount") ||
                            cell.column.id.endsWith("_percentage")),
                      })}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Table.Cell>
                  );
                })}
              </motion.tr>
            ))}
          </Table.Body>
        </Table.Content>
      </Table.Container>
    </div>
  );
};

export const CustomReportView = ({
  periods,
  showComments = false,
  percentageMode,
  groupBy,
  reportId,
  chartData,
  chartRef,
}: {
  periods?: Record<string, ReportPeriod>;
  showComments?: boolean;
  percentageMode?: string | null;
  groupBy?: string | null;
  reportId: string;
  chartData: CustomReportChartResponse;
  chartRef?: RefObject<HTMLDivElement | null>;
}) => {
  const periodEntries = Object.entries(periods || {});

  const hasGroups = periodEntries.some(
    ([_, period]) => period.groups && period.groups.length > 0
  );

  if (!hasGroups || !chartData) return <TemplateEmptyState />;

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <CustomReportBarChart chartData={chartData} containerRef={chartRef} />

      <MultiPeriodTable
        periodEntries={periodEntries}
        showComments={showComments}
        percentageMode={percentageMode}
        groupBy={groupBy}
        reportId={reportId}
      />
    </div>
  );
};

const TemplateEmptyState = () => (
  <EmptyMetrics
    illustration={
      <div className="t-text-neutral-20 t-my-2">
        <CustomReportEmptyStateIcon />
      </div>
    }
    title={<div className="t-text-text-60">Build your Custom template</div>}
    subtitle="Select period, categories, configure groupings, and generate insights tailored to your needs."
  />
);

export const DEFAULT_CHART_CONFIG: ChartConfig = {
  chart_type: CHART_TYPE.BAR,
  x_axis: "PERIOD",
  y_axis: ANALYSIS_MODE.AMOUNT,
  series: { mode: SERIES_MODE.GROUPS, group_id: null },
};

const SaveReportModal = ({
  isOpen,
  close,
  entityId,
  defaultTitle,
  titleOnly = false,
  filter,
  chartConfig,
  groups = [],
  onSaved,
}: {
  isOpen: boolean;
  close: () => void;
  entityId: string;
  defaultTitle?: string;
  titleOnly?: boolean;
  filter?: ReportFilterType;
  chartConfig?: ChartConfig;
  groups?: PreviewGroup[];
  onSaved?: () => void;
}) => {
  const { alertToast, successToast } = useToast();
  const [updateCustomReport, { isLoading }] = useUpdateCustomReportMutation();
  const [title, setTitle] = useState(defaultTitle || "");
  const query = useQuery();

  useEffect(() => {
    if (isOpen && defaultTitle) {
      setTitle(defaultTitle);
    }
  }, [isOpen, defaultTitle]);

  const reportId = query.get("reportId") || "";
  const history = useHistory();
  const location = useLocation();
  const { link } = useConstructInternalLink();

  const isLiveFinancialsContext = location.pathname.includes("live-financials");

  const handleSave = async () => {
    try {
      const payload = titleOnly
        ? { title }
        : {
            title,
            start_date: filter?.START_DATE,
            end_date: filter?.END_DATE,
            accounting_method: filter?.ACCOUNTING_METHOD,
            comparison_report_type: filter?.VIEW_BY,
            percentage_mode: filter?.PERCENTAGE_MODE,
            chart_config: chartConfig ?? DEFAULT_CHART_CONFIG,
            groups: groups.map(({ uuid, title: groupTitle, category_ids }) => ({
              uuid,
              title: groupTitle,
              category_ids,
            })),
          };

      await updateCustomReport({ entityId, reportId, payload }).unwrap();

      successToast({ message: "Report saved successfully" });
      handleClose();
      onSaved?.();

      if (!titleOnly) {
        const targetUrl = isLiveFinancialsContext
          ? `/books/live-financials/custom-report/${reportId}`
          : `/books/custom-reports/${reportId}`;
        history.push(link(targetUrl));
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const handleClose = () => {
    setTitle("");
    close();
  };

  const modalTitle = titleOnly ? "Edit report title" : "Save report";

  return (
    <Modal.Root open={isOpen} onOpenChange={handleClose}>
      <Modal.Content size="small">
        <Modal.Header>
          <Modal.Title>{modalTitle}</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="t-flex t-flex-col t-gap-4">
          <div className="t-flex t-flex-col t-gap-1.5">
            <Label required>Report title</Label>
            <BareInput
              placeholder="Add title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Button customType="secondary" size="regular" onClick={handleClose}>
            Back
          </Button>
          <Button
            customType="primary"
            size="regular"
            onClick={handleSave}
            disabled={!title.trim()}
            isLoading={isLoading}
          >
            {titleOnly ? "Save title" : "Save report"}
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};

const CustomReportContent = ({
  initialFilter,
  initialGroups,
  initialChartConfig,
  reportTitle,
}: {
  initialFilter: ReportFilterType;
  initialGroups: PreviewGroup[];
  initialChartConfig: ChartConfig;
  reportTitle: string;
}) => {
  const { link } = useConstructInternalLink();
  const history = useHistory();
  const location = useLocation();
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroup();
  const query = useQuery();
  const reportId = query.get("reportId") || "";
  const categoryGroupModal = useModal();
  const saveReportModal = useModal();
  const editTitleModal = useModal();
  const [editingGroup, setEditingGroup] = useState<PreviewGroup | null>(null);
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [updateCustomReport, { isLoading: isSaving }] =
    useUpdateCustomReportMutation();
  const [groups, setGroups] = useState<PreviewGroup[]>(initialGroups);
  const [isDirty, setIsDirty] = useState(false);

  const {
    values: filter,
    updateFilter,
    updateMultipleFilters,
  } = useFilters({
    initialValue: initialFilter,
  });
  const [chartConfig, setChartConfig] =
    useState<ChartConfig>(initialChartConfig);

  const isEditingReport = query.get("mode") === CUSTOM_REPORT_MODE.EDIT;
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.FINANCIALS
  );

  const [previewReport, { data: customReport, isLoading }] =
    usePreviewCustomReportMutation();

  const [previewChart, { data: chartResponse, isLoading: isChartLoading }] =
    usePreviewCustomReportChartMutation();

  const buildPreviewPayload = (
    currentFilter: ReportFilterType,
    currentGroups: PreviewGroup[]
  ) => ({
    entityId,
    reportId,
    start_date: currentFilter.START_DATE,
    end_date: currentFilter.END_DATE,
    show_non_zero_accounts: currentFilter.HIDE_ZERO ?? false,
    accounting_method: currentFilter.ACCOUNTING_METHOD,
    comparison_report_type: currentFilter.VIEW_BY,
    percentage_mode: currentFilter.PERCENTAGE_MODE,
    groups: currentGroups.map(({ uuid, title, category_ids }) => ({
      uuid,
      title,
      category_ids,
    })),
  });

  const callPreviews = (
    currentFilter: ReportFilterType,
    currentGroups: PreviewGroup[],
    currentChartConfig: ChartConfig
  ) => {
    if (!reportId) return;
    const base = buildPreviewPayload(currentFilter, currentGroups);
    previewReport(base);
    previewChart({
      ...base,
      chart_config: currentChartConfig,
    });
  };

  useEffect(() => {
    callPreviews(filter, groups, chartConfig);
  }, [reportId]);

  const handleFilterUpdate: typeof updateFilter = (name, newValue) => {
    setIsDirty(true);
    updateFilter(name, newValue);
    const updatedFilter = { ...filter, [name]: newValue };
    callPreviews(updatedFilter, groups, chartConfig);
  };

  const handleMultipleFilterUpdate = (updates: Partial<ReportFilterType>) => {
    setIsDirty(true);
    updateMultipleFilters(updates);
    callPreviews({ ...filter, ...updates }, groups, chartConfig);
  };

  const handleChartConfigChange = (update: Partial<ChartConfig>) => {
    setIsDirty(true);
    const newConfig = { ...chartConfig, ...update };

    if (!filter.PERCENTAGE_MODE && newConfig.y_axis === ANALYSIS_MODE.PERCENT) {
      newConfig.y_axis = ANALYSIS_MODE.AMOUNT;
    }

    setChartConfig(newConfig);
    callPreviews(filter, groups, newConfig);
  };

  const handleGroupSave = (group: PreviewGroup) => {
    setIsDirty(true);
    let updatedGroups: PreviewGroup[];

    if (group.uuid) {
      updatedGroups = groups.map((g) => (g.uuid === group.uuid ? group : g));
    } else {
      const dummyUuid = `${randomBytes(10).toString("hex")}NEW_GROUP`;
      updatedGroups = [...groups, { ...group, uuid: dummyUuid }];
    }

    setGroups(updatedGroups);
    callPreviews(filter, updatedGroups, chartConfig);
  };

  const handleDeleteGroup = (groupUuid: string) => {
    setIsDirty(true);
    const updatedGroups = groups.filter((g) => g.uuid !== groupUuid);
    setGroups(updatedGroups);

    let updatedChartConfig = chartConfig;
    if (chartConfig.series.group_id === groupUuid) {
      updatedChartConfig = {
        ...chartConfig,
        series: { mode: SERIES_MODE.GROUPS, group_id: null },
      };
      setChartConfig(updatedChartConfig);
    }

    callPreviews(filter, updatedGroups, updatedChartConfig);
  };

  const handleEditGroup = (group: PreviewGroup) => {
    setEditingGroup(group);
    categoryGroupModal.open();
  };

  const handleCategoryGroupModalClose = () => {
    setEditingGroup(null);
    categoryGroupModal.close();
  };

  const { alertToast, successToast } = useToast();

  const { isUnsavedModalOpen, handleClose, handleExit, handleSave } =
    useUnsavedChanges({
      isDirty,
    });

  const handleDirectSave = async () => {
    try {
      await updateCustomReport({
        entityId,
        reportId,
        payload: {
          title: reportTitle,
          start_date: filter.START_DATE,
          end_date: filter.END_DATE,
          accounting_method: filter.ACCOUNTING_METHOD,
          comparison_report_type: filter.VIEW_BY,
          percentage_mode: filter.PERCENTAGE_MODE,
          chart_config: chartConfig ?? DEFAULT_CHART_CONFIG,
          groups: groups.map(({ uuid, title, category_ids }) => ({
            uuid,
            title,
            category_ids,
          })),
        },
      }).unwrap();
      setIsDirty(false);
      handleSave();
      successToast({ message: "Report saved successfully" });
      const targetUrl = isLiveFinancialsContext
        ? `/books/live-financials/custom-report/${reportId}`
        : `/books/custom-reports/${reportId}`;
      history.push(link(targetUrl));
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const handleSaveClick = async () => {
    if (isEditingReport) {
      await handleDirectSave();
    } else {
      saveReportModal.open();
    }
  };

  const handleUnsavedModalSave = async () => {
    await handleSaveClick();
    handleSave();
  };

  const isLiveFinancialsContext = location.pathname.includes("live-financials");

  const customReportsLink = isLiveFinancialsContext
    ? link("/books/live-financials/others")
    : link("/books/custom-reports");

  const breadcrumbName = isLiveFinancialsContext
    ? "Report List"
    : "Custom Reports";

  const titleWithPencil = isEditingReport ? (
    <div className="t-flex t-items-center t-gap-2">
      <span>{reportTitle || "New Template"}</span>
      <Button
        customType="ghost_icon"
        size="small"
        onClick={editTitleModal.open}
      >
        <EditPencil />
      </Button>
    </div>
  ) : (
    reportTitle || "New Template"
  );

  return (
    <DashboardContainer className="t-h-full t-w-full t-flex t-px-10 sm:t-px-3 sm:t-h-auto sm:t-overflow-x-visible">
      <DashboardContainer.Header className="t-sticky t-top-0 t-bg-white sm:t-static">
        <Header
          v2
          title={titleWithPencil}
          breadcrumbs={[{ name: breadcrumbName, link: customReportsLink }]}
          right={
            <div className="t-flex t-items-center t-gap-2">
              {isEditingReport ? (
                <div className="t-flex t-items-center t-gap-2">
                  <CustomReportDownload
                    reportId={reportId}
                    isGroupByView={
                      filter.VIEW_BY === PERIOD.MONTHLY ||
                      filter.VIEW_BY === PERIOD.QUARTERLY ||
                      filter.VIEW_BY === PERIOD.YEARLY
                    }
                    filter={filter}
                    isEditingReport={isEditingReport}
                    savePayload={{
                      title: reportTitle,
                      groups,
                      chartConfig,
                    }}
                    onSaved={() => setIsDirty(false)}
                    chartRef={chartRef}
                    isChartReady={!isChartLoading && !!chartResponse}
                  />
                  <LockedUi
                    feature={WRITE_PERMISSION_KEYS.FINANCIALS}
                    blockedUi={<LockedButton text="Save" />}
                  >
                    <Button
                      customType="primary"
                      size="small"
                      isLoading={isSaving}
                      onClick={handleSaveClick}
                    >
                      Save
                    </Button>
                  </LockedUi>
                </div>
              ) : (
                <LockedUi
                  feature={WRITE_PERMISSION_KEYS.FINANCIALS}
                  blockedUi={<LockedButton text="Save Report" />}
                >
                  <Button
                    customType="secondary"
                    size="small"
                    onClick={saveReportModal.open}
                  >
                    Save Report
                  </Button>
                </LockedUi>
              )}
            </div>
          }
        />
      </DashboardContainer.Header>
      <DashboardContainer.Content className="t-flex t-flex-col t-flex-1 t-min-h-0 sm:t-overflow-visible">
        <div className="t-py-4 sm:t-flex sm:t-flex-wrap sm:t-items-center sm:t-gap-2">
          <ReportFilter
            reportType={INCOME_STATEMENT}
            filter={filter}
            updateFilter={handleFilterUpdate}
            onMultipleFilterUpdate={handleMultipleFilterUpdate}
            entityId={entityId}
            isGroupByView={false}
            showClassificationInFilters={false}
            hideViewBy
          />
        </div>
        <div className="t-flex t-flex-1 t-min-h-0 t-h-full sm:t-flex-col sm:t-flex-none sm:t-h-auto">
          <ConfigureSidebar
            onAddCategoryGroup={() => categoryGroupModal.open()}
            groups={groups}
            onEditGroup={handleEditGroup}
            onDeleteGroup={handleDeleteGroup}
            filter={filter}
            updateFilter={handleFilterUpdate}
            chartConfig={chartConfig}
            onChartConfigChange={handleChartConfigChange}
            hasWritePermission={hasWritePermission}
          />
          <div className="t-flex-1 t-overflow-auto t-pb-5 sm:t-flex-none sm:t-min-w-0">
            {isLoading || isChartLoading ? (
              <Loader />
            ) : (
              <CustomReportView
                periods={customReport?.periods}
                percentageMode={filter.PERCENTAGE_MODE}
                groupBy={filter.VIEW_BY}
                reportId={reportId}
                chartData={chartResponse!}
                chartRef={chartRef}
              />
            )}
          </div>
        </div>
      </DashboardContainer.Content>

      <CategoryGroupModal
        key={editingGroup?.uuid || "new"}
        isOpen={categoryGroupModal.isOpen}
        close={handleCategoryGroupModalClose}
        entityId={entityId}
        groupId={groupId!}
        editGroup={editingGroup}
        onSave={handleGroupSave}
      />

      <SaveReportModal
        isOpen={saveReportModal.isOpen}
        close={saveReportModal.close}
        entityId={entityId}
        defaultTitle={reportTitle}
        filter={filter}
        chartConfig={chartConfig}
        groups={groups}
        onSaved={() => {
          setIsDirty(false);
          handleSave();
        }}
      />

      <SaveReportModal
        isOpen={editTitleModal.isOpen}
        close={editTitleModal.close}
        entityId={entityId}
        defaultTitle={reportTitle}
        titleOnly
      />

      <UnsavedChangesModal
        isOpen={isUnsavedModalOpen}
        onClose={handleClose}
        onExit={handleExit}
        onSave={handleUnsavedModalSave}
        isSaving={isSaving}
      />
    </DashboardContainer>
  );
};

export const NewCustomReportTemplate = () => {
  const entityId = useCurrentEntityId();
  const query = useQuery();
  const reportId = query.get("reportId") || "";
  const mode = query.get("mode");

  const isTemplateMode = mode === CUSTOM_REPORT_MODE.TEMPLATE;

  const { data: customReportsList, isLoading: isCustomReportsListLoading } =
    useGetCustomReportsQuery(
      { entityId },
      { skip: !entityId || isTemplateMode }
    );

  const { data: templateReport, isLoading: isTemplateLoading } =
    useGetCustomReportByIdQuery(
      { entityId, reportId },
      { skip: !entityId || !reportId || !isTemplateMode }
    );

  const reportConfig = customReportsList?.find((r) => r.uuid === reportId);

  const initialFilter: ReportFilterType = {
    ...DEFAULT_FILTER,
    START_DATE: reportConfig?.start_date
      ? dayjs(reportConfig.start_date).format(YYYY_MM_DD)
      : DEFAULT_FILTER.START_DATE,
    END_DATE: reportConfig?.end_date
      ? dayjs(reportConfig.end_date).format(YYYY_MM_DD)
      : DEFAULT_FILTER.END_DATE,
    ACCOUNTING_METHOD:
      (reportConfig?.accounting_method as ReportFilterType["ACCOUNTING_METHOD"]) ||
      DEFAULT_FILTER.ACCOUNTING_METHOD,
    VIEW_BY:
      (reportConfig?.comparison_report_type as ReportFilterType["VIEW_BY"]) ||
      PERIOD.TOTALS,
    PERCENTAGE_MODE: reportConfig?.percentage_mode || null,
  };

  const initialGroups: PreviewGroup[] = isTemplateMode
    ? (templateReport?.category_groups ?? []).map(
        ({ uuid, title, categories }) => ({
          uuid,
          title,
          category_ids: categories.map(({ uuid }) => uuid),
        })
      )
    : reportConfig?.groups ?? [];

  const initialChartConfig: ChartConfig =
    reportConfig?.chart_config ?? DEFAULT_CHART_CONFIG;

  const isLoading = isTemplateMode
    ? isTemplateLoading
    : isCustomReportsListLoading;

  if (reportId && isLoading) {
    return <Loader />;
  }

  return (
    <CustomReportContent
      initialFilter={initialFilter}
      initialGroups={initialGroups}
      initialChartConfig={initialChartConfig}
      reportTitle={reportConfig?.title ?? ""}
    />
  );
};
